import TextInput from "../../molecules/inputs/TextInput";
import TextAreaInput from "../../molecules/inputs/TextAreaInput";
import UploadImage from "../../molecules/UploadImage";
import { useState, useEffect, useRef } from "react";
import axios from "axios";
import api from "../../../Api";

interface SidePopUpProps {
  Title?: string;
  SubTitle?: string;
  isPopUpOpen: boolean;
  SubmitButtonText?: string;
  onClose: () => void;
  onTestimonialAdded: (testimonial: Testimonial) => void;
}

interface Testimonial {
  _id: string;  // Add _id to the Testimonial interface
  Id: string;
  Img: string;
  Name: string;
  Position: string;
  Description: string;
}

export default function SidePopUp({
  Title = "Add Testimonial",
  SubTitle = "Add testimonial you want to add for the word of praise section",
  isPopUpOpen = false,
  SubmitButtonText = "Add a testimonial",
  onClose,
  onTestimonialAdded,
}: SidePopUpProps) {
  const [inputValue, setInputValue] = useState("");
  const [position, setPosition] = useState("");
  const [textareaValue, setTextareaValue] = useState("");
  const [uploadedImage, setUploadedImage] = useState<File | string>("");
  const [error, setError] = useState("");

  const popupRef = useRef<HTMLDivElement>(null);

  const handleInputChange = (value: string) => {
    setInputValue(value);
  };

  const handlePositionInputChange = (value: string) => {
    setPosition(value);
  };

  const handleTextareaChange = (value: string) => {
    setTextareaValue(value);
  };

  const handleImageUpload = (file: File) => {
    setUploadedImage(file); // Accept the file object
  };

  const handleSubmit = async () => {
    if (!uploadedImage || typeof uploadedImage === "string") {
      alert("Please upload an image.");
      return;
    }

    const formData = new FormData();
    formData.append("file", uploadedImage);

    try {
      const uploadResponse = await axios.post(api.uploadFile, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (uploadResponse.data.success) {
        const uploadedImageUrl = uploadResponse.data.url;

        // Generate a unique ID for the new testimonial (you can change this logic as needed)
        const testimonialData: Testimonial = {
          _id: `${Date.now()}`, // Include _id
          Id: `${Date.now()}`,
          Img: uploadedImageUrl,
          Name: inputValue,
          Position: position,
          Description: textareaValue,
        };

        const response = await axios.post(api.testimonial, testimonialData, {
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (response.data.success) {
          onTestimonialAdded(testimonialData); // Now passing the full testimonial object with _id
          onClose();
        } else {
          setError("Something went wrong");
        }
      } else {
        setError("Something went wrong");
      }
    } catch (error) {
      console.error("Error adding testimonial:", error);
    }
  };

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    if (isPopUpOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
    }

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [isPopUpOpen, onClose]);

  return (
    <div
      ref={popupRef}
      className={`bg-white z-10 border-l space-y-4 py-4 fixed ${isPopUpOpen ? "right-0" : "right-[-500px]"} h-full duration-300 w-[400px]`}
    >
      <div className="px-4 space-y-4">
        <div className="space-y-2">
          <h1 className="font-primary text-title">{Title}</h1>
          <p className="text-black/[.60]">{SubTitle}</p>
        </div>

        <UploadImage
          ButtonText="Upload an image"
          onUpload={handleImageUpload} // Pass the file object to the parent
        />

        <TextInput
          Name="Name"
          Placeholder="Enter testimonial name"
          Value={inputValue}
          onChange={handleInputChange}
        />

        <TextInput
          Name="Position"
          Placeholder="Enter testimonial position"
          Value={position}
          onChange={handlePositionInputChange}
        />

        <TextAreaInput
          Name="Testimonial"
          Placeholder="Write your testimonial"
          Value={textareaValue}
          onChange={handleTextareaChange}
        />
      </div>

      <hr />

      <div className="flex items-center justify-end space-x-4 px-4">
        <p onClick={onClose} className="text-black/[.60] cursor-pointer">
          cancel
        </p>
        <div
          onClick={handleSubmit}
          className="bg-primary text-white hover:bg-primary_dark duration-300 p-2 px-4 rounded hover:cursor-pointer"
        >
          <p>{SubmitButtonText}</p>
        </div>
      </div>

      {error && (
        <div className="text-red-500 text-center mt-2">{error}</div>
      )}
    </div>
  );
}
